import { useEffect, useRef, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import Swal from 'sweetalert2';
import { postRequest, putRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  Button,
  Grid
} from '@mui/material';
import Modal from 'react-bootstrap/Modal';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

function MyVerticallyCenteredModal({ show, onHide, authToken, onCustomerAdd, selectedPurchasers = [] }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedPurchasers.some(c => c._id === customerId);
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    try {
      setCreating(true);
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || '',
        dob: '',
        address: {
          address1: '',
          address2: '',
          town: '',
          country: '',
          postcode: ''
        }
      };

      const res = await postRequest('private/createCustomer', payload, authToken);

      if (res?.status === 200 && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully!',
          timer: 1500,
          showConfirmButton: false
        });

        // Add the newly created customer
        onCustomerAdd(res.data);

        // Reset search fields
        setSearchFields({
          title: '',
          forename: '',
          surname: '',
          email: '',
          phone_number: ''
        });

        // Close modal
        onHide();
      } else {
        throw new Error(res?.message || 'Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to create customer'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Purchaser</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <Box sx={{ py: 2 }}>
                        <div style={{ marginBottom: '10px', color: '#666' }}>
                          No customers found
                        </div>
                        {(searchFields.email || searchFields.phone_number || searchFields.forename || searchFields.surname) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'Add Customer'}
                          </Button>
                        )}
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

const EditConveyancingPurchase = () => {
  const { lead_id, slug } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  
  console.log('🔄 EditConveyancingPurchase Component Loading...');
  console.log('🔄 Component Type: PURCHASE DETAILS FORM (COMPLEX UI)');
  console.log('🔄 Params:', { lead_id, slug });
  console.log('🔄 Current URL:', window.location.href);

  const [selectedPurchasers, setSelectedPurchasers] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [customerError, setCustomerError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedLenderPanelId, setSelectedLenderPanelId] = useState('');
  const [leadMongoId, setLeadMongoId] = useState(null); // 🎯 Store MongoDB _id for update API

  // Info checkboxes state
  const [infoOptions, setInfoOptions] = useState({
    firstTimeBuyer: false,
    helpToBuyISA: false,
    helpToBuy: false,
    helpToBuyEquityLoan: false,
    rightToBuy: false,
    sharedOwnership: false,
    buyingWithMortgage: false,
    newBuild: false,
    islamicMortgage: false,
    auctionRepossession: false,
    giftedDeposit: 0,
    multipleProperties: false,
    buyToLet: false,
    arrangingIndemnityPolicy: false,
    unregisteredProperty: false,
    staircasing: false,
    nonUKResident: false,
    nonResidentialMixedUse: false
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      address1: '',
      address2: '',
      town: '',
      county: '',
      postcode: '',
      purchasePrice: '',
      ownershipType: 'freehold',
      completionDate: (() => {
        // Default to today + 3 months
        const date = new Date();
        date.setMonth(date.getMonth() + 3);
        return date.toISOString().split('T')[0];
      })()
    },
  });

  // Postcode branch validation states
  const [postcodeMatch, setPostcodeMatch] = useState(null);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [isPostcodeValid, setIsPostcodeValid] = useState(false); // Track if postcode is approved

  // Watch postcode field
  const postcodeValue = watch('postcode');

  const handleCustomerAdd = (customer) => {
    if (!selectedPurchasers.some((c) => c._id === customer._id)) {
      setSelectedPurchasers((prev) => [...prev, customer]);
      setCustomerError(false);
    }
  };

  // Check postcode in branches when postcode changes
  useEffect(() => {
    const checkPostcode = async () => {
      if (!postcodeValue || postcodeValue.trim().length < 3) {
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);
        return;
      }

      try {
        setCheckingPostcode(true);

        // STEP 1: Check if postcode exists via checkPostcodeInBranch API
        console.log('🔍 Step 1: Checking if postcode exists');
        const checkRes = await postRequest(
          'private/checkPostcodeInBranch',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 1 Result:', checkRes);

        // Check if Step 1 failed (invalid postcode)
        if (checkRes?.status === 406 || checkRes?.data?.exists === false) {
          // ❌ Step 1 Failed: Invalid postcode (not in UK postcode database)
          console.log('❌ Step 1 Failed: Postcode does not exist');
          setPostcodeMatch(null);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(false);

          // Show error popup
          Swal.fire({
            title: 'Invalid Postcode',
            text: checkRes?.message || 'This postcode does not exist in the UK postcode database. Please enter a valid UK postcode.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
          return; // Stop here, don't proceed to Step 2
        }

        // ✅ Step 1 Passed: Postcode exists
        console.log('✅ Step 1 Passed: Postcode exists');
        console.log('🔍 Step 2: Matching branch by postcode prefix');

        // STEP 2: Match branch by postcode prefix (only runs if Step 1 passed)
        const matchRes = await postRequest(
          'private/matchBranchByPostcode',
          { postcode: postcodeValue.trim() },
          authCtx.token
        );

        console.log('📡 Step 2 Result:', matchRes);

        if (matchRes?.data?.matched) {
          // ✅ Branch matched
          console.log('✅ Branch matched:', matchRes.data.branch);
          setPostcodeMatch(matchRes.data.branch);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(true);
        } else {
          // ❌ Step 2 Failed: Valid postcode but no branch serves this area
          console.log('❌ No branch serves this postcode area');
          setPostcodeMatch(null);
          setAvailableBranches([]);
          setSelectedBranch(null);
          setIsPostcodeValid(false);
          Swal.fire({
            title: 'No Branch Coverage',
            text: 'This postcode area is not currently served by any of our branches.',
            icon: 'warning',
            confirmButtonText: 'OK',
            confirmButtonColor: '#6f42c1'
          });
        }
      } catch (error) {
        console.error('Error checking postcode:', error);
        setPostcodeMatch(null);
        setAvailableBranches([]);
        setSelectedBranch(null);
        setIsPostcodeValid(false);
      } finally {
        setCheckingPostcode(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(checkPostcode, 500);
    return () => clearTimeout(timeoutId);
  }, [postcodeValue, authCtx.token]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      console.log('=== AUTO-FILL DEBUG START (PURCHASE) ===');
      console.log('Lead ID:', lead_id);
      console.log('Auth token exists:', !!authCtx.token);

      // Get Purchase conveyancing specific data using getConveyancingDetails
      const res = await postRequest(`private/getConveyancingDetails/${lead_id}`, {}, authCtx.token);
      console.log('API Response:', res);
      console.log('API Response Status:', res?.status);
      console.log('API Response Data:', res?.data);

      const lead = res?.data;

      if (!lead) {
        console.log('❌ NO LEAD DATA FOUND');
        console.log('Trying alternative API...');

        // Try old API as fallback
        const altRes = await postRequest(`private/getEditDataPurchaseConveyancing/${lead_id}`, {}, authCtx.token);
        console.log('Alternative API Response:', altRes);
        
        if (altRes?.status === 'success' && altRes?.data) {
          const altLead = altRes.data;
          console.log('✅ GOT DATA FROM ALTERNATIVE API:', altLead);
          // 🎯 Store MongoDB _id for update API
          if (altLead._id) {
            setLeadMongoId(altLead._id);
            console.log('✅ Stored MongoDB _id:', altLead._id);
          }
          // Use alternative lead data
          await performAutoFill(altLead);
          return;
        }
      }

      if (lead) {
        console.log('✅ LEAD DATA EXISTS, starting auto-fill...');
        // 🎯 Store MongoDB _id for update API
        if (lead._id) {
          setLeadMongoId(lead._id);
          console.log('✅ Stored MongoDB _id:', lead._id);
        }
        await performAutoFill(lead);
      } else {
        console.log('❌ NO LEAD DATA FROM ANY API');
        Swal.fire('Error', 'Could not load lead data for editing', 'error');
      }
    } catch (err) {
      console.error('❌ AUTO-FILL ERROR:', err);
      Swal.fire('Error', 'Failed to load lead data: ' + err.message, 'error');
    } finally {
      setLoading(false);
      console.log('=== AUTO-FILL DEBUG END ===');
    }
  };

  const performAutoFill = async (lead) => {
    console.log('🚀 PERFORMING AUTO-FILL WITH DATA:', lead);
    
    try {
      // Property Address Auto-fill with detailed logging
      // ✅ FIX: For PURCHASE, use PROPERTY address, NOT customer's personal address
      // Priority: lead.address (property) > property_address > propertyDetails.address
      let addressSource = null;

      // Use property address (the house being PURCHASED)
      if (lead.address || lead.property_address || lead.propertyDetails?.address || lead.purchasePropertyDetails?.address) {
        addressSource = lead.address || lead.property_address || lead.propertyDetails?.address || lead.purchasePropertyDetails?.address;
        console.log('📍 Using property address (CORRECT for Purchase)');
      }
      // Only if property address doesn't exist, fallback to customer address
      else if (lead.customers && lead.customers.length > 0 && lead.customers[0].address) {
        addressSource = lead.customers[0].address;
        console.log('⚠️ Fallback: Using customer address (property address not found)');
      }
      // Last resort: purchaser address
      else if (lead.purchasers && lead.purchasers.length > 0 && lead.purchasers[0].address) {
        addressSource = lead.purchasers[0].address;
        console.log('⚠️ Fallback: Using purchaser address (no other address found)');
      }

      console.log('📍 Processing address:', addressSource);
      console.log('📍 propertyDetails:', lead.propertyDetails);
      console.log('📍 purchasePropertyDetails:', lead.purchasePropertyDetails);
      console.log('📍 property_address:', lead.property_address);
      console.log('📍 address:', lead.address);
      console.log('📍 customers:', lead.customers);
      console.log('📍 purchasers:', lead.purchasers);

      if (addressSource) {
        if (typeof addressSource === 'string') {
          console.log('📍 String address detected:', addressSource);
          const parts = addressSource.split(',').map(p => p.trim());
          console.log('📍 Address parts:', parts);

          setValue('address1', parts[0] || '');
          setValue('address2', parts[1] || '');
          setValue('town', parts[2] || '');
          setValue('county', parts[3] || '');
          setValue('postcode', parts[parts.length - 1] || '');

          console.log('✅ String address fields set');
        } else if (typeof addressSource === 'object') {
          console.log('📍 Object address detected:', addressSource);

          setValue('address1', addressSource.line1 || addressSource.address1 || '');
          setValue('address2', addressSource.line2 || addressSource.address2 || '');
          setValue('town', addressSource.town || addressSource.city || '');
          setValue('county', addressSource.county || addressSource.country || addressSource.state || '');
          setValue('postcode', addressSource.postcode || addressSource.postal_code || '');

          console.log('✅ Object address fields set');
          console.log('📍 Address values:', {
            address1: addressSource.line1 || addressSource.address1,
            address2: addressSource.line2 || addressSource.address2,
            town: addressSource.town || addressSource.city,
            county: addressSource.county || addressSource.country,
            postcode: addressSource.postcode || addressSource.postal_code
          });
        }
      } else {
        console.log('⚠️ No address found in lead data');
      }

      // Purchase Price Auto-fill with detailed logging
      const price = lead.property_value || lead.net_value || lead.purchasePropertyDetails?.purchasePrice || '';
      console.log('💰 Setting purchase price:', price, 'from', {
        property_value: lead.property_value,
        net_value: lead.net_value,
        purchasePropertyDetails_price: lead.purchasePropertyDetails?.purchasePrice
      });
      setValue('purchasePrice', String(price));
      console.log('✅ Purchase price set successfully');

      // Ownership Type Auto-fill - convert to lowercase for schema compatibility
      let ownership = lead.ownership_type || lead.purchasePropertyDetails?.ownershipType || (lead.leasehold ? 'leasehold' : 'freehold');
      // Normalize to lowercase to match schema enum: ['freehold','leasehold','share_of_freehold']
      if (ownership) {
        ownership = ownership.toLowerCase().replace(/\s+/g, '_');
      }
      console.log('🏡 Setting ownership type:', ownership, 'from ownership_type:', lead.ownership_type, 'leasehold flag:', lead.leasehold);
      setValue('ownershipType', ownership);
      console.log('✅ Ownership type set successfully');

      // Completion Date Auto-fill
      const completionDateSource = lead.completion_date || lead.purchasePropertyDetails?.completionDate;
      if (completionDateSource) {
        console.log('📅 Processing completion date:', completionDateSource);
        try {
          const date = new Date(completionDateSource);
          const formattedDate = date.toISOString().split('T')[0];
          console.log('📅 Formatted date:', formattedDate);
          setValue('completionDate', formattedDate);
          console.log('✅ Completion date set successfully');
        } catch (err) {
          console.log('❌ Date parsing error:', err);
        }
      } else {
        // Auto-fill with today + 3 months if no completion_date exists
        console.log('⚠️ No completion_date found, setting default (today + 3 months)');
        const today = new Date();
        today.setMonth(today.getMonth() + 3);
        const formattedDate = today.toISOString().split('T')[0];
        console.log('📅 Default date set:', formattedDate);
        setValue('completionDate', formattedDate);
      }

      // Purchasers Auto-fill with detailed logging
      console.log('👥 Processing customers/purchasers...');
      console.log('👥 Available customer data:', {
        purchasers: lead.purchasers,
        customers: lead.customers,
        customer_ids: lead.customer_ids
      });

      let purchasersToSet = [];
      let customersToMerge = [];

      // 🎯 Collect purchasers from lead.purchasers
      if (lead.purchasers && Array.isArray(lead.purchasers)) {
        purchasersToSet = lead.purchasers;
        console.log('✅ Found purchasers:', purchasersToSet.length);
      }

      // 🎯 Collect customers from lead.customers
      if (lead.customers && Array.isArray(lead.customers)) {
        customersToMerge = lead.customers;
        console.log('✅ Found customers:', customersToMerge.length);
      } else if (lead.customer_ids && Array.isArray(lead.customer_ids) && purchasersToSet.length === 0) {
        // Only fetch by IDs if we don't have direct data
        console.log('🔍 Fetching customers by IDs:', lead.customer_ids.length);

        try {
          const customerPromises = lead.customer_ids.map(async (id) => {
            console.log('🔍 Fetching customer ID:', id);
            try {
              const customerRes = await postRequest(`private/getCustomerIndividualData/${id}`, {}, authCtx.token);
              console.log('👤 Customer response for', id, ':', customerRes);
              return customerRes?.data || null;
            } catch (err) {
              console.log('❌ Error fetching customer', id, ':', err);
              return null;
            }
          });

          const customers = await Promise.all(customerPromises);
          customersToMerge = customers.filter(c => c && c._id);
          console.log('✅ Customers fetched successfully:', customersToMerge.length);
        } catch (err) {
          console.log('❌ Error in customer fetching process:', err);
        }
      }

      // 🎯 Merge customers and purchasers, removing duplicates by _id
      const mergedPurchasers = [...purchasersToSet];
      console.log('🔀 Starting merge - Initial purchasers:', mergedPurchasers.length);

      customersToMerge.forEach(customer => {
        const exists = mergedPurchasers.some(p => p._id === customer._id);
        if (!exists) {
          mergedPurchasers.push(customer);
          console.log('✅ Added customer to purchasers:', customer.forename, customer.surname, 'ID:', customer._id);
        } else {
          console.log('⚠️ Customer already in purchasers list:', customer.forename, customer.surname);
        }
      });

      if (mergedPurchasers.length === 0) {
        console.log('⚠️ No customer/purchaser data found in lead');
      } else {
        console.log('✅ Total purchasers after merge:', mergedPurchasers.length);
        console.log('✅ Final merged list:');
        mergedPurchasers.forEach((p, idx) => {
          console.log(`  ${idx + 1}. ${p.title} ${p.forename} ${p.surname} (${p._id})`);
        });
      }

      setSelectedPurchasers(mergedPurchasers);
      console.log('✅ Purchasers state updated with merged data');

      // Info Options Auto-fill with detailed logging
      console.log('📊 Processing info options...');

      // Check purchasePropertyDetails.info first, then fall back to root level
      const infoSource = lead.purchasePropertyDetails?.info || lead.info || lead;
      console.log('📊 Info source:', infoSource);
      console.log('📊 purchasePropertyDetails.info:', lead.purchasePropertyDetails?.info);
      console.log('📊 Available boolean fields in lead:', {
        first_time_buyer: infoSource.firstTimeBuyer || lead.first_time_buyer,
        help_to_buy_isa: infoSource.helpToBuyISA || lead.help_to_buy_isa,
        help_to_buy: infoSource.helpToBuy || lead.help_to_buy,
        shared_ownership: infoSource.sharedOwnership || lead.shared_ownership,
        new_build: infoSource.newBuild || lead.new_build,
        mortgage_lender: infoSource.buyingWithMortgage || lead.mortgage_lender
      });

      const infoData = {
        firstTimeBuyer: Boolean(infoSource.firstTimeBuyer || lead.first_time_buyer),
        helpToBuyISA: Boolean(infoSource.helpToBuyISA || lead.help_to_buy_isa),
        helpToBuy: Boolean(infoSource.helpToBuy || lead.help_to_buy),
        helpToBuyEquityLoan: Boolean(infoSource.helpToBuyEquityLoan || lead.help_to_buy_equity_loan),
        rightToBuy: Boolean(infoSource.rightToBuy || lead.right_to_buy),
        sharedOwnership: Boolean(infoSource.sharedOwnership || lead.shared_ownership),
        buyingWithMortgage: Boolean(infoSource.buyingWithMortgage || lead.mortgage_lender),
        newBuild: Boolean(infoSource.newBuild || lead.new_build),
        islamicMortgage: Boolean(infoSource.islamicMortgage || lead.islamic_mortgage),
        auctionRepossession: Boolean(infoSource.auctionRepossession || lead.auction_repossession),
        giftedDeposit: parseInt(infoSource.giftedDeposit || lead.gifted_deposit || 0),
        multipleProperties: Boolean(infoSource.multipleProperties || lead.multiple_properties),
        buyToLet: Boolean(infoSource.buyToLet || lead.buy_to_let),
        arrangingIndemnityPolicy: Boolean(infoSource.arrangingIndemnityPolicy || lead.arranging_indemnity_policy),
        unregisteredProperty: Boolean(infoSource.unregisteredProperty || lead.unregistered_property),
        staircasing: Boolean(infoSource.staircasing || lead.staircasing),
        nonUKResident: Boolean(infoSource.isNonUKResident || lead.non_uk_resident || lead.is_non_uk_resident),
        nonResidentialMixedUse: Boolean(infoSource.nonResidentialMixed || lead.non_residential_mixed_use)
      };
      
      console.log('📊 Processed info options:', infoData);
      console.log('📊 Active options:', Object.keys(infoData).filter(k => infoData[k]));
      setInfoOptions(infoData);
      console.log('✅ Info options state updated');

      // Partner Auto-fill
      if (lead.partner_id) {
        const partnerId = typeof lead.partner_id === 'object' ? lead.partner_id._id : lead.partner_id;
        setSelectedPartnerId(partnerId);
        console.log('  🤝 Partner ID:', partnerId);
      }

      // Lender Panel Auto-fill
      if (lead.lender_panel_id) {
        const lenderPanelId = typeof lead.lender_panel_id === 'object' ? lead.lender_panel_id._id : lead.lender_panel_id;
        setSelectedLenderPanelId(lenderPanelId);
        console.log('  🏦 Lender Panel ID:', lenderPanelId);
      }

      // Force form update after a short delay
      setTimeout(() => {
        console.log('🔄 Forcing form update...');

        // Use same addressSource logic as above
        let resetAddressSource = null;

        // ✅ FIX: Use property address first (same as auto-fill logic)
        if (lead.address || lead.property_address || lead.propertyDetails?.address || lead.purchasePropertyDetails?.address) {
          resetAddressSource = lead.address || lead.property_address || lead.propertyDetails?.address || lead.purchasePropertyDetails?.address;
          console.log('🔄 Using property address for reset (CORRECT)');
        }
        // Fallback to customer address only if property address missing
        else if (lead.customers && lead.customers.length > 0 && lead.customers[0].address) {
          resetAddressSource = lead.customers[0].address;
          console.log('🔄 Fallback: Using customer address for reset');
        }
        // Last resort: purchaser address
        else if (lead.purchasers && lead.purchasers.length > 0 && lead.purchasers[0].address) {
          resetAddressSource = lead.purchasers[0].address;
          console.log('🔄 Fallback: Using purchaser address for reset');
        }

        let resetAddress = {
          address1: '',
          address2: '',
          town: '',
          county: '',
          postcode: ''
        };

        if (resetAddressSource) {
          if (typeof resetAddressSource === 'string') {
            const parts = resetAddressSource.split(',').map(p => p.trim());
            resetAddress = {
              address1: parts[0] || '',
              address2: parts[1] || '',
              town: parts[2] || '',
              county: parts[3] || '',
              postcode: parts[parts.length - 1] || ''
            };
          } else if (typeof resetAddressSource === 'object') {
            resetAddress = {
              address1: resetAddressSource.line1 || resetAddressSource.address1 || '',
              address2: resetAddressSource.line2 || resetAddressSource.address2 || '',
              town: resetAddressSource.town || resetAddressSource.city || '',
              county: resetAddressSource.county || resetAddressSource.country || resetAddressSource.state || '',
              postcode: resetAddressSource.postcode || resetAddressSource.postal_code || ''
            };
          }
        }

        console.log('🔄 Reset address values:', resetAddress);

        const completionDateSource = lead.completion_date || lead.purchasePropertyDetails?.completionDate;
        let completionDateForReset = '';
        if (completionDateSource) {
          completionDateForReset = new Date(completionDateSource).toISOString().split('T')[0];
        } else {
          // Default to today + 3 months if no completion date
          const defaultDate = new Date();
          defaultDate.setMonth(defaultDate.getMonth() + 3);
          completionDateForReset = defaultDate.toISOString().split('T')[0];
        }

        const formValues = {
          ...resetAddress,
          purchasePrice: String(price),
          ownershipType: ownership,
          completionDate: completionDateForReset
        };
        console.log('🔄 Form values for reset:', formValues);
        reset(formValues);
      }, 500);

      console.log('🎉 AUTO-FILL COMPLETED SUCCESSFULLY!');
    } catch (err) {
      console.error('❌ ERROR IN PERFORM AUTO-FILL:', err);
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setPartners(res.data.docs);
        console.log('✅ Partners loaded:', res.data.docs.length);
      }
    } catch (err) {
      console.error('❌ Error fetching partners:', err);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const res = await postRequest('private/lenderPanelList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setLenderPanels(res.data.docs);
        console.log('✅ Lender panels loaded:', res.data.docs.length);
      }
    } catch (err) {
      console.error('❌ Error fetching lender panels:', err);
    }
  };

  useEffect(() => {
    if (lead_id) {
      fetchLeadDetails();
    }
    fetchPartners();
    fetchLenderPanels();
  }, [lead_id]);

  const onSubmit = async (formData) => {
    if (selectedPurchasers.length === 0) {
      setCustomerError(true);
      return;
    }

    // 🎯 Check if we have MongoDB _id
    if (!leadMongoId) {
      Swal.fire('Error', 'Lead ID not found. Please refresh and try again.', 'error');
      return;
    }

    // Block save until both postcode checks (UK database + branch match) have passed
    if (checkingPostcode) {
      Swal.fire({
        icon: 'info',
        title: 'Please Wait',
        text: 'Postcode verification is still in progress. Please try again in a moment.'
      });
      return;
    }

    if (!isPostcodeValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Postcode',
        text: 'Please enter a valid UK postcode that is covered by one of our branches before saving.'
      });
      return;
    }

    // 🎯 Ensure ownershipType is lowercase
    const ownershipType = formData.ownershipType ? formData.ownershipType.toLowerCase().replace(/\s+/g, '_') : 'freehold';
    console.log('🔍 Form ownershipType:', formData.ownershipType, '→ Normalized:', ownershipType);

    // 🎯 NEW PAYLOAD FORMAT for updateConveyancingPurchase API
    const customerIds = selectedPurchasers.map((c) => c._id);
    const payload = {
      conveyancing_id: leadMongoId, // MongoDB _id

      purchasers: customerIds,
      customers: customerIds, // Both purchasers and customers are same for purchase conveyancing

      propertyDetails: {
        address: {
          address1: formData.address1,
          address2: formData.address2,
          town: formData.town,
          county: formData.county,
          postcode: formData.postcode
        },
        propertyValue: parseFloat(formData.purchasePrice),
        completionDate: formData.completionDate,
        ownershipType: ownershipType
      },

      info: {
        // 🎯 FIX: Use correct camelCase field names from infoOptions state
        firstTimeBuyer: infoOptions.firstTimeBuyer || false,
        helpToBuyISA: infoOptions.helpToBuyISA || false,
        helpToBuy: infoOptions.helpToBuy || false,
        helpToBuyEquityLoan: infoOptions.helpToBuyEquityLoan || false,
        rightToBuy: infoOptions.rightToBuy || false,
        sharedOwnership: infoOptions.sharedOwnership || false,
        buyingWithMortgage: infoOptions.buyingWithMortgage || false,
        newBuild: infoOptions.newBuild || false,
        islamicMortgage: infoOptions.islamicMortgage || false,
        auctionRepossession: infoOptions.auctionRepossession || false,
        giftedDeposit: infoOptions.giftedDeposit || 0,
        multipleProperties: infoOptions.multipleProperties || false,
        buyToLet: infoOptions.buyToLet || false,
        arrangingIndemnityPolicy: infoOptions.arrangingIndemnityPolicy || false,
        unregisteredProperty: infoOptions.unregisteredProperty || false,
        staircasing: infoOptions.staircasing || false,
        isNonUKResident: infoOptions.nonUKResident || false,
        nonResidentialMixed: infoOptions.nonResidentialMixedUse || false
      },

      partner_id: selectedPartnerId || undefined,
      lender_panel_id: selectedLenderPanelId || undefined
    };

    try {
      console.log('🚀 Submitting Purchase Update:', payload);
      // ✅ FIX: Use lead_id (L-12) instead of MongoDB _id - backend supports both
      const res = await putRequest(`private/editConveyancingPurchase/${lead_id}`, payload, authCtx.token);
      console.log('📥 Response:', res);

      if (res?.status === 'success' || res?.status === 200) {
        Swal.fire('Success', 'Conveyancing purchase updated successfully!', 'success');
        navigate('/leads');
      } else {
        const errorMsg = res?.message || res?.data?.message || 'Update failed';
        console.error('❌ Update failed:', res);
        Swal.fire('Error', errorMsg, 'error');
      }
    } catch (err) {
      console.error('❌ Error submitting:', err);
      const errorMsg = err?.response?.data?.message || err?.message || 'Something went wrong';
      Swal.fire('Error', errorMsg, 'error');
    }
  };

  const handleInfoToggle = (key) => {
    setInfoOptions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleGiftedDepositChange = (e) => {
    setInfoOptions(prev => ({
      ...prev,
      giftedDeposit: parseInt(e.target.value) || 0
    }));
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />

      <div className="page-header pb-3 mb-3">
        <h1 className="mt-0 mb-0" style={{ fontSize: '1.5rem', fontWeight: '500' }}>
          Edit Conveyancing - Purchase #{lead_id}
        </h1>
      </div>

      <div style={{ backgroundColor: '#e9ecef', padding: '15px', borderRadius: '4px', marginBottom: '20px' }}>
        <strong>Purchase Details</strong>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="card mb-4" style={{ backgroundColor: '#ffffff' }}>
          <div className="card-body">
            {/* Property Section */}
            <h5 className="mb-4">Property</h5>
            <Row>
              <Col md={6}>
                <h6 className="mb-3">Address</h6>
                <Controller
                  name="address1"
                  control={control}
                  rules={{ required: 'Address is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Address Line 1"
                      fullWidth
                      sx={{ mb: 2 }}
                      error={!!errors.address1}
                      helperText={errors.address1?.message}
                    />
                  )}
                />
                <Controller
                  name="address2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Address Line 2"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="town"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Town/City"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="county"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="County/State"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="postcode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Postcode"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Col>

              <Col md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <h6>Purchase Price *</h6>
                    <Controller
                      name="purchasePrice"
                      control={control}
                      rules={{ required: 'Purchase price is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          fullWidth
                          placeholder="Enter purchase price"
                          slotProps={{
                            input: {
                              startAdornment: <InputAdornment position="start">£</InputAdornment>,
                            }
                          }}
                          error={!!errors.purchasePrice}
                          helperText={errors.purchasePrice?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Ownership Type</h6>
                    <Controller
                      name="ownershipType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <Select {...field} displayEmpty>
                            <MenuItem value="freehold">Freehold</MenuItem>
                            <MenuItem value="leasehold">Leasehold</MenuItem>
                            <MenuItem value="share_of_freehold">Share of Freehold</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Est. Completion Date</h6>
                    <Controller
                      name="completionDate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="date"
                          fullWidth
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Partner</h6>
                    <FormControl fullWidth>
                      <Select
                        value={selectedPartnerId}
                        onChange={(e) => setSelectedPartnerId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Partner</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <h6>Lender Panel</h6>
                    <FormControl fullWidth>
                      <Select
                        value={selectedLenderPanelId}
                        onChange={(e) => setSelectedLenderPanelId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel</em>
                        </MenuItem>
                        {lenderPanels.map((lender) => (
                          <MenuItem key={lender._id} value={lender._id}>
                            {lender.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Col>
            </Row>

            {/* Postcode checking alerts - positioned after Property grid */}
            {checkingPostcode && postcodeValue && (
              <Row className="mt-3">
                <Col>
                  <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                    Checking postcode in branches...
                  </Alert>
                </Col>
              </Row>
            )}

            {postcodeMatch && !checkingPostcode && (
              <Row className="mt-3">
                <Col>
                  <Alert severity="success" sx={{ fontSize: '0.875rem' }}>
                    ✓ Postcode found in branch: <strong>{postcodeMatch.name}</strong>
                  </Alert>
                </Col>
              </Row>
            )}

            {/* REMOVED: Postcode not found warning and branch selection
            {!postcodeMatch && !checkingPostcode && postcodeValue && postcodeValue.trim().length >= 3 && (
              <Row className="mt-3">
                <Col md={6}>
                  <Alert severity="warning" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    ⚠️ Postcode not found in any branch. Please select a branch:
                  </Alert>
                  {availableBranches.length > 0 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Branch</InputLabel>
                      <Select
                        value={selectedBranch || ''}
                        onChange={(e) => {
                          const branchId = e.target.value;
                          setSelectedBranch(branchId);

                          // Find the selected branch and auto-fill postcode
                          const branch = availableBranches.find(b => b.id === branchId);
                          if (branch && branch.address.postcode) {
                            setValue('postcode', branch.address.postcode);
                          }
                        }}
                        label="Select Branch"
                      >
                        {availableBranches.map((branch) => (
                          <MenuItem key={branch.id} value={branch.id}>
                            {branch.name} - {branch.address.postcode || 'No postcode'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Col>
              </Row>
            )}
            */}

            <hr className="my-4" />

            {/* Purchasers Section */}
            <h5 className="mb-3">Purchasers</h5>
            {customerError && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                Please add at least one purchaser.
              </Alert>
            )}
            <div className="mb-3">
              {selectedPurchasers.length > 0 ? (
                selectedPurchasers.map((purchaser) => (
                  <div
                    key={purchaser._id}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      padding: '12px 15px',
                      marginBottom: '10px',
                      position: 'relative'
                    }}
                  >
                    <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                      {purchaser.title} {purchaser.forename} {purchaser.surname}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d', display: 'flex', gap: '20px' }}>
                      <span>📧 {purchaser.email}</span>
                      <span>📞 {purchaser.phone_number}</span>
                    </div>
                    <CloseIcon
                      style={{
                        cursor: 'pointer',
                        color: '#dc3545',
                        position: 'absolute',
                        right: '15px',
                        top: '15px',
                        fontSize: '20px'
                      }}
                      onClick={() => setSelectedPurchasers(prev => prev.filter(p => p._id !== purchaser._id))}
                    />
                  </div>
                ))
              ) : (
                <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '10px' }}>
                  No purchasers added yet
                </div>
              )}
              <Button
                variant="text"
                onClick={() => setModalShow(true)}
                style={{ color: '#6f42c1' }}
              >
                + ADD PURCHASER
              </Button>
            </div>

            <hr className="my-4" />

            {/* Info Section */}
            <h5 className="mb-3">Info</h5>
          <div className="d-flex flex-wrap gap-2">
            {Object.entries({
              firstTimeBuyer: 'First Time Buyer',
              helpToBuyISA: 'Help to Buy ISA',
              helpToBuy: 'Help to Buy',
              helpToBuyEquityLoan: 'Help to Buy Equity Loan',
              rightToBuy: 'Right to Buy',
              sharedOwnership: 'Shared Ownership',
              buyingWithMortgage: 'Buying with Mortgage',
              newBuild: 'New Build',
              islamicMortgage: 'Islamic Mortgage',
              auctionRepossession: 'Auction / Repossession',
              multipleProperties: 'Multiple Properties',
              buyToLet: 'Buy to Let',
              arrangingIndemnityPolicy: 'Arranging an Indemnity Policy',
              unregisteredProperty: 'Unregistered Property',
              staircasing: 'Staircasing',
              nonUKResident: 'Is Non-UK Resident',
              nonResidentialMixedUse: 'Non-Residential / Mixed Use'
            }).map(([key, label]) => (
              <Button
                key={key}
                size="small"
                variant={infoOptions[key] ? "contained" : "outlined"}
                onClick={() => handleInfoToggle(key)}
                style={{
                  backgroundColor: infoOptions[key] ? '#6f42c1' : 'transparent',
                  color: infoOptions[key] ? 'white' : '#6f42c1',
                  borderColor: '#6f42c1',
                  textTransform: 'none',
                  padding: '4px 12px',
                  fontSize: '13px',
                  borderRadius: '4px'
                }}
              >
                {label}
              </Button>
            ))}

            {/* Gifted Deposit - Num Giftors */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              border: '1px solid #6f42c1',
              borderRadius: '4px',
              padding: '4px 12px',
              fontSize: '13px'
            }}>
              <span style={{ marginRight: '10px', color: '#6f42c1' }}>
                Gifted Deposit
              </span>
              <input
                type="number"
                value={infoOptions.giftedDeposit}
                onChange={handleGiftedDepositChange}
                min="0"
                style={{
                  width: '50px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '13px',
                  padding: '0',
                  textAlign: 'center'
                }}
              />
            </div>
          </div>

            <Row className="mb-4 mt-4">
              <Col>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={!isPostcodeValid || checkingPostcode}
                    style={{
                      backgroundColor: !isPostcodeValid || checkingPostcode ? '#cccccc' : '#6f42c1',
                      color: !isPostcodeValid || checkingPostcode ? '#666666' : 'white',
                      textTransform: 'none',
                      padding: '10px 30px'
                    }}
                  >
                    SAVE
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    style={{
                      borderColor: '#6c757d',
                      color: '#6c757d',
                      textTransform: 'none',
                      padding: '10px 30px'
                    }}
                  >
                    CANCEL
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </Form>

      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        authToken={authCtx.token}
        onCustomerAdd={handleCustomerAdd}
        selectedPurchasers={selectedPurchasers}
      />
    </>
  );
};

export default EditConveyancingPurchase;